import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, DataPage, qk } from "../_common";
import { createMcpToken, listMcpTokens, revokeMcpToken, type McpTokenItem } from "./mcp-client";
import { buildMcpSetupInstructions } from "./mcp-setup-instructions";

const TOKEN_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : TOKEN_DATE_FORMAT.format(date);
}

export function McpSettingsPage() {
  const queryClient = useQueryClient();
  const [codeInput, setCodeInput] = useState("");
  const [managementCode, setManagementCode] = useState("");
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [setupBundle, setSetupBundle] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const tokensQuery = useQuery({
    queryKey: qk.mcp.tokens,
    queryFn: () => listMcpTokens(managementCode),
    enabled: managementCode.length > 0,
  });
  const createMutation = useMutation({
    mutationFn: (tokenLabel: string) => createMcpToken(managementCode, tokenLabel),
    onSuccess: (created) => {
      setSetupBundle(buildMcpSetupInstructions(window.location.origin, created.token));
      setLabel("");
      void queryClient.invalidateQueries({ queryKey: qk.mcp.tokens });
    },
    gcTime: 0,
  });
  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) => revokeMcpToken(managementCode, tokenId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.mcp.tokens }),
  });

  const tokens: McpTokenItem[] = tokensQuery.data ?? [];
  const errorMessage = tokensQuery.error instanceof Error
    ? tokensQuery.error.message
    : createMutation.error instanceof Error
      ? createMutation.error.message
      : revokeMutation.error instanceof Error
        ? revokeMutation.error.message
        : null;

  async function unlock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const candidate = codeInput;
    if (!candidate) return;
    setUnlockError(null);
    try {
      const tokens = await queryClient.fetchQuery({ queryKey: qk.mcp.tokens, queryFn: () => listMcpTokens(candidate), staleTime: 0 });
      queryClient.setQueryData(qk.mcp.tokens, tokens);
      setManagementCode(candidate);
    } catch {
      setManagementCode("");
      queryClient.removeQueries({ queryKey: qk.mcp.tokens });
      setUnlockError("The code was rejected or MCP token management is not configured.");
    }
  }

  function lock() {
    setManagementCode("");
    setCodeInput("");
    setSetupBundle(null);
    createMutation.reset();
    revokeMutation.reset();
    queryClient.removeQueries({ queryKey: qk.mcp.tokens });
  }

  async function copySetupBundle() {
    if (!setupBundle) return;
    try {
      await navigator.clipboard.writeText(setupBundle);
      setCopyMessage("Connection instructions copied. Paste them into your AI chat.");
    } catch {
      setCopyMessage("Clipboard access failed. Select and copy the text below.");
    }
  }

  return (
    <DataPage title="MCP connections">
      {!managementCode ? (
        <form onSubmit={(event) => void unlock(event)} className="max-w-lg space-y-4">
          <p className="text-sm text-muted-foreground">Enter the service management code configured by the owner to manage AI connections.</p>
          {unlockError ? <p role="alert" className="text-sm text-destructive">{unlockError}</p> : null}
          <label className="block space-y-1 text-sm font-medium">
            Management code
            <input
              type="password"
              autoComplete="current-password"
              value={codeInput}
              onChange={(event) => setCodeInput(event.target.value)}
              className="h-10 w-full rounded-md border bg-background px-3"
              required
            />
          </label>
          <Button type="submit" disabled={tokensQuery.isFetching}>Continue</Button>
        </form>
      ) : (
        <div className="space-y-6">
          {errorMessage ? <p role="alert" className="text-sm text-destructive">{errorMessage}</p> : null}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">Create one token for each AI client. You can revoke a connection here at any time.</p>
            <Button variant="outline" onClick={lock}>Lock settings</Button>
          </div>

          {setupBundle ? (
            <Card className="border-amber-500/50">
              <CardHeader>
                <CardTitle className="text-lg">Copy setup for your AI</CardTitle>
                <CardDescription>
                  This message contains the token. Pasting it into an AI chat may save it in that chat's history. The token is shown only now; close this panel after copying.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button onClick={() => void copySetupBundle()}>Copy all instructions and connection details</Button>
                {copyMessage ? <p role="status" className="text-sm text-muted-foreground">{copyMessage}</p> : null}
                <details>
                  <summary className="cursor-pointer text-sm text-muted-foreground">Show copied text</summary>
                  <textarea readOnly value={setupBundle} className="mt-2 h-80 w-full rounded-md border bg-muted p-3 font-mono text-xs" />
                </details>
                <Button variant="outline" onClick={() => { setSetupBundle(null); createMutation.reset(); }}>Close token</Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Create connection</CardTitle>
                <CardDescription>A new token grants this AI client read and write access to the service portfolio.</CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    createMutation.mutate(label.trim() || "AI client");
                  }}
                  className="flex flex-wrap items-end gap-3"
                >
                  <label className="min-w-64 flex-1 space-y-1 text-sm font-medium">
                    Connection name
                    <input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={100} className="h-10 w-full rounded-md border bg-background px-3" placeholder="My AI assistant" />
                  </label>
                  <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? "Creating…" : "Create token"}</Button>
                </form>
              </CardContent>
            </Card>
          )}

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Connections</h2>
            {tokensQuery.isFetching ? <p className="text-sm text-muted-foreground">Loading connections…</p> : null}
            {!tokensQuery.isFetching && tokens.length === 0 ? <p className="text-sm text-muted-foreground">No MCP connections yet.</p> : null}
            <ul className="divide-y rounded-md border">
              {tokens.map((token) => (
                <li key={token.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <div>
                    <p className="font-medium">{token.label}</p>
                    <p className="text-xs text-muted-foreground">Created {formatDate(token.createdAt)}{token.revokedAt ? ` · Revoked ${formatDate(token.revokedAt)}` : ""}</p>
                  </div>
                  {token.revokedAt ? (
                    <span className="text-sm text-muted-foreground">Revoked</span>
                  ) : (
                    <Button variant="destructive" disabled={revokeMutation.isPending} onClick={() => revokeMutation.mutate(token.id)}>
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </DataPage>
  );
}
